import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Loader2, CheckCircle2, XCircle, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AcceptInvitation() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState("");

  useEffect(() => {
    api.get(`/invitations/${token}`).then((r) => setInfo(r.data)).catch((e) => setLoadError(formatApiError(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    setLoading(true);
    try {
      const r = await api.post("/auth/accept-invitation", { token, password, confirm_password: confirm });
      setDone(r.data.message);
    } catch (err) {
      setSubmitError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const card = (children) => (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-8 flex items-center gap-2"><img src="/brand/procdeal-logo.png" alt="ProcDeal" className="h-8 w-auto" /></Link>
        {children}
      </div>
    </div>
  );

  if (loadError) {
    return card(
      <div data-testid="invite-invalid" className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <XCircle className="mx-auto h-10 w-10 text-red-500" />
        <p className="mt-3 font-heading text-lg font-semibold text-red-900">Invitation unavailable</p>
        <p className="mt-1 text-sm text-red-700">{loadError}</p>
        <Link to="/login" className="mt-4 inline-block text-sm font-medium text-blue-700 hover:underline">Go to sign in</Link>
      </div>
    );
  }
  if (!info) {
    return card(<div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>);
  }
  if (done) {
    return card(
      <div data-testid="invite-success" className="rounded-lg border border-green-200 bg-green-50 p-6 text-center">
        <CheckCircle2 className="mx-auto h-10 w-10 text-[#22C55E]" />
        <p className="mt-3 font-heading text-lg font-semibold text-green-900">{done}</p>
        <Button data-testid="invite-goto-login" className="mt-4 bg-slate-900 hover:bg-slate-800" onClick={() => (window.location.href = "/login")}>Sign in</Button>
      </div>
    );
  }
  if (info.status !== "pending" || info.expired) {
    return card(
      <div data-testid="invite-expired" className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-center">
        <XCircle className="mx-auto h-10 w-10 text-amber-500" />
        <p className="mt-3 font-heading text-lg font-semibold text-amber-900">
          {info.expired ? "This invitation link has expired" : "This invitation link has already been used or revoked"}
        </p>
        <p className="mt-1 text-sm text-amber-700">Ask your Enterprise Admin to send a new invitation.</p>
        <Link to="/login" className="mt-4 inline-block text-sm font-medium text-blue-700 hover:underline">Go to sign in</Link>
      </div>
    );
  }

  return card(
    <>
      <div className="flex items-center gap-2">
        <Building2 className="h-6 w-6" />
        <h1 className="font-heading text-2xl font-semibold tracking-tight" data-testid="invite-title">Join {info.enterprise_name}</h1>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        You've been invited as a <span className="font-medium text-slate-900">Buyer</span>. Set your password to activate your account, then sign in.
      </p>
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="invite-form">
        <div>
          <Label htmlFor="inv-email">Email</Label>
          <Input id="inv-email" data-testid="invite-email" value={info.email} disabled className="mt-1.5 bg-slate-50" />
        </div>
        <div>
          <Label htmlFor="inv-pw">Password</Label>
          <Input id="inv-pw" data-testid="invite-password" type="password" required minLength={8} value={password}
            onChange={(e) => setPassword(e.target.value)} placeholder="Min. 8 characters" className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="inv-cpw">Confirm password</Label>
          <Input id="inv-cpw" data-testid="invite-confirm-password" type="password" required value={confirm}
            onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat password" className="mt-1.5" />
        </div>
        {submitError && <p data-testid="invite-error" role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{submitError}</p>}
        <Button type="submit" data-testid="invite-submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Set password & activate account
        </Button>
      </form>
    </>
  );
}
